import React, { useState, useEffect, useRef } from 'react';
import { useGarden } from '@/context/GardenContext';
import { PLANTS } from '@/data/plants';
import type { Plant } from '@/types/plant';
import type { GardenPlant } from '@/types/garden';
import { useHemisphere } from '@/context/HemisphereContext';
import './AddPlantModal.css';

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

interface AddPlantModalProps {
  /** Pass a pre-selected plant to skip the search step */
  plant?: Plant;
  onClose: () => void;
}

export default function AddPlantModal({ plant: initialPlant, onClose }: AddPlantModalProps) {
  const { dataMonth } = useHemisphere();
  // The six-month shift is its own inverse, so the same mapping turns a
  // northern data month back into the reader's own month.
  const monthList = (months: number[] | undefined) =>
    (months ?? []).map(m => MONTH_ABBR[dataMonth(m) - 1]).join(', ');

  const { addPlant, garden } = useGarden();
  const [step, setStep] = useState<'search' | 'details'>(initialPlant ? 'details' : 'search');
  const [selected, setSelected] = useState<Plant | null>(initialPlant ?? null);
  const [search, setSearch] = useState('');
  const [saved, setSaved] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<Omit<GardenPlant, 'id' | 'plantId' | 'plantName'>>({
    quantity: 1,
    status: 'planned',
    plotArea: '',
    datePlanted: '',
    notes: '',
  });

  useEffect(() => {
    if (step === 'search') searchRef.current?.focus();
  }, [step]);

  // Trap focus in modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = PLANTS.filter(p =>
    search.trim() === '' || p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.family.toLowerCase().includes(search.toLowerCase()) ||
    p.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  // Check if already in garden
  const alreadyAdded = (plantId: string) =>
    garden?.plants.some(p => p.plantId === plantId) ?? false;

  function selectPlant(p: Plant) {
    setSelected(p);
    setStep('details');
  }

  function submit() {
    if (!selected) return;
    addPlant({
      plantId: selected.id,
      plantName: selected.name,
      ...form,
    });
    setSaved(true);
    setTimeout(onClose, 900);
  }

  return (
    <div className="apm-overlay" role="dialog" aria-modal="true" aria-label="Add plant to garden" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="apm-modal paper">

        {/* Header */}
        <div className="apm-header">
          <div className="apm-header-left">
            {step === 'details' && !initialPlant && (
              <button className="apm-back" onClick={() => { setStep('search'); setSelected(null); }} aria-label="Back to search">
                ←
              </button>
            )}
            <h2 className="text-title apm-title">
              {step === 'search' ? '🌱 Add a plant' : `Add ${selected?.name} to my garden`}
            </h2>
          </div>
          <button className="apm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ---- SEARCH STEP ---- */}
        {step === 'search' && (
          <div className="apm-search-step">
            <input
              ref={searchRef}
              className="input apm-search-input"
              type="search"
              placeholder="Search for a plant — tomato, carrot, kale…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />

            <div className="apm-results">
              {filtered.length === 0 ? (
                <div className="apm-empty">
                  <p className="text-hand" style={{ color: 'var(--ink-faint)' }}>No plants found for "{search}"</p>
                </div>
              ) : (
                filtered.map(p => {
                  const added = alreadyAdded(p.id);
                  return (
                    <button
                      key={p.id}
                      className={`apm-result-item ${added ? 'apm-result-item--added' : ''}`}
                      onClick={() => !added && selectPlant(p)}
                      disabled={added}
                    >
                      <span className="apm-result-emoji">{p.emoji}</span>
                      <div className="apm-result-info">
                        <span className="text-hand apm-result-name">{p.name}</span>
                        <span className="text-caption apm-result-latin">{p.latinName}</span>
                      </div>
                      <div className="apm-result-meta">
                        <span className={`badge badge-${p.biodynamicDay === 'root' ? 'root' : p.biodynamicDay === 'flower' ? 'flower' : p.biodynamicDay === 'fruit' ? 'fruit' : 'leaf'}`}>
                          {p.biodynamicDay}
                        </span>
                        {added && <span className="text-caption apm-added-label">Already added</span>}
                        {!added && <span className="apm-result-arrow">→</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ---- DETAILS STEP ---- */}
        {step === 'details' && selected && (
          <div className="apm-details-step">
            {/* Plant summary */}
            <div className="apm-plant-summary">
              <span className="apm-plant-emoji">{selected.emoji}</span>
              <div>
                <p className="text-hand-lg" style={{ color: 'var(--ink)' }}>{selected.name}</p>
                <p className="text-caption" style={{ color: 'var(--ink-faint)', fontStyle: 'italic' }}>{selected.latinName}</p>
              </div>
            </div>

            <div className="apm-fields">
              <div className="apm-row">
                <div className="field">
                  <label>Quantity / plants</label>
                  <input
                    className="input"
                    type="number"
                    min="1"
                    max="10000"
                    value={form.quantity}
                    onChange={e => setForm(f => ({ ...f, quantity: Number(e.target.value) }))}
                  />
                </div>

                <div className="field">
                  <label>Status</label>
                  <select
                    className="select input"
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value as GardenPlant['status'] }))}
                  >
                    <option value="planned">🗓 Planned</option>
                    <option value="sown">🌱 Sown / germinating</option>
                    <option value="growing">🌿 Growing</option>
                    <option value="harvesting">✂️ Harvesting</option>
                    <option value="finished">✓ Finished</option>
                  </select>
                </div>
              </div>

              <div className="apm-row">
                <div className="field">
                  <label>Date planted / sown</label>
                  <input
                    className="input"
                    type="date"
                    value={form.datePlanted ?? ''}
                    onChange={e => setForm(f => ({ ...f, datePlanted: e.target.value }))}
                  />
                </div>

                <div className="field">
                  <label>Location in garden</label>
                  <input
                    className="input"
                    type="text"
                    placeholder="e.g. Bed 2, south corner"
                    value={form.plotArea ?? ''}
                    onChange={e => setForm(f => ({ ...f, plotArea: e.target.value }))}
                  />
                </div>
              </div>

              <div className="field">
                <label>Notes</label>
                <textarea
                  className="textarea"
                  rows={3}
                  placeholder={`e.g. Variety: ${selected.name === 'Tomato' ? 'Sungold + Gardeners Delight' : selected.name === 'Carrot' ? 'Nantes 5 + Purple Haze' : 'any variety notes'}`}
                  value={form.notes ?? ''}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>

              {/* Handy reminder from plant data */}
              {selected.calendar.sowIndoors || selected.calendar.sowOutdoors ? (
                <div className="apm-tip">
                  <span className="apm-tip-icon">💡</span>
                  <p className="text-small">
                    <strong>{selected.name}</strong> is typically{' '}
                    {selected.calendar.sowIndoors?.length
                      ? `sown indoors in ${monthList(selected.calendar.sowIndoors)}`
                      : `sown outdoors in ${monthList(selected.calendar.sowOutdoors)}`
                    }{selected.calendar.harvest?.length
                      ? ` and harvested in ${monthList(selected.calendar.harvest)}.`
                      : '.'
                    }
                  </p>
                </div>
              ) : null}
            </div>

            <div className="apm-footer">
              <button className="advice-cat-btn" onClick={onClose}>Cancel</button>
              <button
                className={`btn-sage ${saved ? 'gsf-save-btn--saved' : ''}`}
                onClick={submit}
                disabled={saved}
                style={{ minWidth: 160 }}
              >
                {saved ? '✓ Added!' : `Add ${selected.name} to my garden`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
